import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ClipboardList, Plus, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DeleteButton } from "@/components/DeleteButton";

const EMPTY = { medication_name: "", strength: "" };

export default function OrderLog() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/order-log"); setItems(data); } catch { /* */ } }

  async function submit(e) {
    e.preventDefault();
    if (!form.medication_name || !form.strength) return toast.error("Medication name and strength are required.");
    setLoading(true);
    try {
      await api.post("/order-log", form);
      toast.success("Added to order log.");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function markOrdered(item) {
    try { await api.patch(`/order-log/${item.id}/ordered`); toast.success("Marked as ordered."); load(); }
    catch { toast.error("Failed."); }
  }

  async function del(id) {
    try { await api.delete(`/order-log/${id}`); toast.success("Removed."); load(); }
    catch { toast.error("Failed."); }
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><ClipboardList className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Order Log</h1>
          <p className="text-sm text-muted-foreground">A quick list of medications to order. Mark items ordered once placed in Order History.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5"><Label className="text-xs">Medication name</Label><Input data-testid="ol-name" value={form.medication_name} onChange={(e) => setForm({ ...form, medication_name: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Strength</Label><Input data-testid="ol-strength" placeholder="e.g. 500 mg" value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} required /></div>
        <div className="flex items-end"><Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="ol-submit"><Plus className="mr-1 h-4 w-4" /> Add to Order Log</Button></div>
      </form>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Medication</TableHead><TableHead>Strength</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Nothing to order yet.</TableCell></TableRow>
              : items.map((it) => (
                <TableRow key={it.id} data-testid={`ol-row-${it.id}`} className={it.ordered ? "opacity-70" : ""}>
                  <TableCell className="font-medium">{it.medication_name}</TableCell>
                  <TableCell>{it.strength}</TableCell>
                  <TableCell>
                    {it.ordered
                      ? <Badge className="bg-primary" data-testid={`ol-status-${it.id}`}>Ordered{it.ordered_at ? ` · ${format(new Date(it.ordered_at), "MMM d")}` : ""}</Badge>
                      : <Badge variant="outline" className="border-orange-400 text-orange-600" data-testid={`ol-status-${it.id}`}>To order</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!it.ordered && <Button size="sm" className="rounded-full" onClick={() => markOrdered(it)} data-testid={`ol-mark-${it.id}`}><Check className="mr-1 h-3 w-3" /> Mark as Ordered</Button>}
                      <DeleteButton onConfirm={() => del(it.id)} label="this order-log item" testId={`ol-delete-${it.id}`} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
